import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService, User } from '@lib/core';
import { Notification } from '@lib/models';
import { Pagination } from '@lib/models/api';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs';
import { BrandService } from '../brand/brand.service';
import { BrandMenuItem } from '../../../brands';
import { version } from '../../../../../../package.json';
import { NotificationsService } from '../../notifications/shared/notifications.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit {
  dropdowns: Record<string, boolean> = {};

  drawerOpened = false;
  sidebarCollapsed = false;
  noPaddingPages = ['checkout', 'removals/*/*/details'];
  items: MenuItem[] = [
    {
      label: 'Account',
      icon: 'pi pi-user',
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
    },
  ];

  user: User = new User();
  userNameInitial: string = '';
  paddingClass: string = 'p-5 md:p-8';
  appVersion: string = version;

  // Brand menu items
  menuItems: BrandMenuItem[] = [];

  // Notifications
  notifications: Notification[] = [];
  unreadCount = 0;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public brandService: BrandService,
    private notificationsService: NotificationsService
  ) {
    this._setUserMenu();
    this.authService.user$.subscribe(user => {
      this.user = user;
      this.userNameInitial = user.firstName.charAt(0).toUpperCase();
    });

    this._setLayoutPadding();
    this._initializeMenu();
    this._loadSidebarState();
  }

  private _loadSidebarState() {
    const saved = localStorage.getItem('sidebarCollapsed');
    this.sidebarCollapsed = saved === 'true';
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    localStorage.setItem('sidebarCollapsed', String(this.sidebarCollapsed));
  }

  openDrawer() {
    this.drawerOpened = true;
  }

  closeDrawer() {
    this.drawerOpened = false;
  }

  ngOnInit() {
    this._loadUnreadCount();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.closeDrawer();
        this._loadUnreadCount();
      });
  }

  private _loadUnreadCount() {
    this.notificationsService.getUnreadCount().subscribe({
      next: count => (this.unreadCount = count ?? 0),
      error: () => {},
    });
  }

  // Load the recent notifications when the bell panel opens.
  loadNotifications() {
    this.notificationsService
      .getPaged(new Pagination({ page: 1, limit: 10 }))
      .subscribe({
        next: result => (this.notifications = result.data),
        error: () => {},
      });
  }

  onNotificationClick(notification: Notification) {
    if (!notification.read) {
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      notification.read = true;
      this.notificationsService
        .markAsRead(notification.id)
        .subscribe({ error: () => {} });
    }

    const target = notification.relativeLink ?? notification.link;
    if (target) {
      this.router.navigateByUrl(target);
    }
  }

  markAllRead() {
    this.notifications = this.notifications.map(n => ({ ...n, read: true }) as Notification);
    this.unreadCount = 0;
    this.notificationsService.markAllRead().subscribe({ error: () => {} });
  }

  // TODO: Temp solution , better to use a layout service
  private _setLayoutPadding() {
    this.router.events.subscribe(() => {
      const noPaddingPage = this._isNoPaddingPage(this.router.url);
      this.paddingClass = noPaddingPage ? 'p-0' : 'p-5 md:p-8';
    });
  }

  private _isNoPaddingPage(url: string): boolean {
    return this.noPaddingPages.some(pattern => {
      if (pattern.includes('*')) {
        const regexPattern = pattern
          .replace(/\*/g, '[^/]+')
          .replace(/\//g, '\\/');
        const regex = new RegExp(regexPattern);
        return regex.test(url);
      }
      return url.includes(pattern);
    });
  }

  toggleDropdown(name: string) {
    this.dropdowns[name] = !this.dropdowns[name];
  }

  private _setUserMenu() {
    this.items = [
      {
        label: 'Account',
        icon: 'pi pi-user',
        routerLink: '/profile',
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this._logout(),
      },
    ];
  }

  private _logout() {
    this.authService.signOut();
  }

  private _initializeMenu() {
    this.menuItems = this.brandService.getMenuItems();
    // Initialize dropdowns for menu items with children
    this.menuItems.forEach(item => {
      if (item.children && item.children.length > 0) {
        this.dropdowns[item.id] = false;
      }
    });
  }

  /**
   * Check if a menu item has visible children
   */
  hasVisibleChildren(item: BrandMenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }
}
